/**
 * This file contains initialization code for Capacitor plugins
 * that needs to run as early as possible in the app lifecycle.
 */
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import logger from './shared/utils/logger';

/**
 * Configure the native status bar so the app header sits correctly relative to
 * the notch / status bar on each platform. iOS relies on CSS safe-area insets
 * (viewport-fit=cover + env(safe-area-inset-*)); Android keeps the status bar
 * non-overlaid because its WebView reports safe-area insets as 0.
 */
async function initStatusBar(): Promise<void> {
  const platform = Capacitor.getPlatform();
  try {
    // Dark icons/text for our white header (Style.Light = dark content).
    await StatusBar.setStyle({ style: Style.Light });

    if (platform === 'android') {
      // OS reserves the status-bar area so it visually matches the white header.
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
    }
  } catch (error: unknown) {
    logger.error('[CapacitorInit] Error configuring status bar:', error);
  }
}

// Immediately notify app ready to prevent update rollbacks
// This must be called as early as possible in the app lifecycle
(async function initCapacitor() {
  if (Capacitor.isNativePlatform()) {
    // Configure the status bar early so the header/notch layout is correct on
    // first paint. Runs independently of the OTA-update flow below.
    void initStatusBar();

    try {
      logger.log('[CapacitorInit] Immediately notifying app ready');
      await CapacitorUpdater.notifyAppReady();
      logger.log('[CapacitorInit] Successfully notified app ready');
      
      // Log current bundle info
      try {
        const currentBundle = await CapacitorUpdater.current();
        logger.log('[CapacitorInit] Current bundle:', JSON.stringify(currentBundle));
      } catch (bundleError: unknown) {
        logger.error('[CapacitorInit] Error getting current bundle:', bundleError);
      }
    } catch (error: unknown) {
      logger.error('[CapacitorInit] Error notifying app ready:', error);
      
      // Retry once after a short delay
      setTimeout(async () => {
        try {
          logger.log('[CapacitorInit] Retrying notifyAppReady');
          await CapacitorUpdater.notifyAppReady();
          logger.log('[CapacitorInit] Retry successful');
        } catch (retryError: unknown) {
          logger.error('[CapacitorInit] Retry failed:', retryError);
        }
      }, 500);
    }
  }
})();

export default {};

